import type { PostCopy, PostRevision } from "../api/contract";

/** What the owner can read of a post: its fields, and the words and search details of the revision each copy points to. */
export interface PostSide {
  copy: PostCopy;
  revision: Pick<PostRevision, "bodyMd" | "seo"> | null;
}

/** The most lines the update confirmation lists; the rest are counted. */
export const MAX_CHANGE_LINES = 6;

type Block = { kind: "heading" | "paragraph" | "block"; text: string; under: string };

const DIRECTIVE_NAME: Record<string, string> = { photo: "photo", embed: "video or post", bookmark: "link card", button: "button" };

/** The post's words as blocks (heading, paragraph or a photo, video, card or button), each knowing the heading above it. */
export function postBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  let under = "";
  for (const raw of markdown.replace(/\r\n/g, "\n").split(/\n{2,}/)) {
    const text = raw.trim();
    if (!text) continue;
    const heading = /^#{1,6}\s+(.*)$/.exec(text);
    if (heading && !text.includes("\n")) {
      under = heading[1]!.trim();
      blocks.push({ kind: "heading", text, under });
    } else if (text.startsWith("::")) blocks.push({ kind: "block", text, under });
    else blocks.push({ kind: "paragraph", text, under });
  }
  return blocks;
}

/** The kinds of change in a pair of block lists, from a longest-common-subsequence walk. */
function diffBlocks(before: Block[], after: Block[]): Array<{ op: "same" | "removed" | "added"; block: Block }> {
  const rows = before.length + 1;
  const cols = after.length + 1;
  const table = Array.from({ length: rows }, () => new Array<number>(cols).fill(0));
  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      table[i]![j] = before[i]!.text === after[j]!.text ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const steps: Array<{ op: "same" | "removed" | "added"; block: Block }> = [];
  let i = 0;
  let j = 0;
  while (i < before.length && j < after.length) {
    if (before[i]!.text === after[j]!.text) { steps.push({ op: "same", block: after[j]! }); i += 1; j += 1; }
    else if (table[i + 1]![j]! >= table[i]![j + 1]!) { steps.push({ op: "removed", block: before[i]! }); i += 1; }
    else { steps.push({ op: "added", block: after[j]! }); j += 1; }
  }
  for (; i < before.length; i += 1) steps.push({ op: "removed", block: before[i]! });
  for (; j < after.length; j += 1) steps.push({ op: "added", block: after[j]! });
  return steps;
}

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
const indefinite = (count: number, noun: string) => count === 1 ? `${/^[aeiou]/.test(noun) ? "An" : "A"} ${noun}` : `${count} ${noun}s`;
const underText = (under: string) => under ? ` under “${under.replace(/^#+\s*/, "")}”` : "";

/** What changed in the words, one line per kind of change and place: "A paragraph rewritten under “Washing the ash”". */
export function bodyChanges(liveMd: string, workingMd: string): string[] {
  if (liveMd === workingMd) return [];
  const steps = diffBlocks(postBlocks(liveMd), postBlocks(workingMd));
  const lines = new Map<string, { count: number; line: (count: number) => string }>();
  const note = (key: string, line: (count: number) => string) => {
    const known = lines.get(key);
    if (known) known.count += 1; else lines.set(key, { count: 1, line });
  };
  for (let index = 0; index < steps.length;) {
    const step = steps[index]!;
    if (step.op === "same") { index += 1; continue; }
    // A run of removals followed by additions is a rewrite of the same number of blocks, then what is left over.
    const removed: Block[] = [];
    const added: Block[] = [];
    while (steps[index]?.op === "removed") { removed.push(steps[index]!.block); index += 1; }
    while (steps[index]?.op === "added") { added.push(steps[index]!.block); index += 1; }
    const paired = Math.min(removed.length, added.length);
    for (let at = 0; at < paired; at += 1) {
      const to = added[at]!;
      const from = removed[at]!;
      if (to.kind === "heading" && from.kind === "heading") note("heading", (count) => `${indefinite(count, "heading")} reworded`);
      else if (to.kind === "paragraph" && from.kind === "paragraph") note(`rewritten:${to.under}`, (count) => `${indefinite(count, "paragraph")} rewritten${underText(to.under)}`);
      else { note(`gone:${from.kind}:${from.text}`, () => describeGone(from)); note(`new:${to.kind}:${to.text}`, () => describeNew(to)); }
    }
    for (const block of removed.slice(paired)) note(`removed:${block.kind}:${block.under}`, (count) => describeGone(block, count));
    for (const block of added.slice(paired)) note(`added:${block.kind}:${block.under}`, (count) => describeNew(block, count));
  }
  return [...lines.values()].map(({ count, line }) => line(count));
}

function directiveName(block: Block): string {
  return DIRECTIVE_NAME[/^::(\w+)/.exec(block.text)?.[1] ?? ""] ?? "block";
}

function describeNew(block: Block, count = 1): string {
  if (block.kind === "heading") return `${indefinite(count, "heading")} added`;
  if (block.kind === "block") return `${indefinite(count, directiveName(block))} added${underText(block.under)}`;
  return `${indefinite(count, "paragraph")} added${underText(block.under)}`;
}

function describeGone(block: Block, count = 1): string {
  if (block.kind === "heading") return `${indefinite(count, "heading")} removed`;
  if (block.kind === "block") return `${indefinite(count, directiveName(block))} removed${underText(block.under)}`;
  return `${indefinite(count, "paragraph")} removed${underText(block.under)}`;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * What the owner has changed in a published post since readers last saw it, in plain words, for the Update post
 * confirmation and the post panel's count. Empty when the post has none (or the words aren't loaded to compare).
 */
export function postChanges(live: PostSide, working: PostSide, tagLabel: (id: string) => string = (id) => id): string[] {
  const lines: string[] = [];
  const a = live.copy;
  const b = working.copy;
  if (a.title !== b.title) lines.push(`New title: “${b.title}”`);
  if (a.slug !== b.slug) lines.push("New web address. The old one keeps working and sends readers to the new one");
  if ((a.summary ?? "") !== (b.summary ?? "")) lines.push("Summary changed");
  if (!a.cover && b.cover) lines.push("New cover photo");
  else if (a.cover && !b.cover) lines.push("Cover photo removed");
  else if (a.cover && b.cover) {
    if (a.cover.assetId !== b.cover.assetId || a.cover.version !== b.cover.version) lines.push("New cover photo");
    else if (a.cover.alt !== b.cover.alt) lines.push("Cover photo description changed");
  }
  const addedTags = b.tags.filter((id) => !a.tags.includes(id));
  const removedTags = a.tags.filter((id) => !b.tags.includes(id));
  if (addedTags.length) lines.push(`${addedTags.length === 1 ? "Tag" : "Tags"} added: ${addedTags.map(tagLabel).join(", ")}`);
  if (removedTags.length) lines.push(`${removedTags.length === 1 ? "Tag" : "Tags"} removed: ${removedTags.map(tagLabel).join(", ")}`);
  if (!addedTags.length && !removedTags.length && !same(a.tags, b.tags)) lines.push("Main tag changed");
  if ((a.publishAt ?? "") !== (b.publishAt ?? "")) lines.push("Publish date changed");
  if (a.revision !== b.revision && live.revision && working.revision) {
    lines.push(...bodyChanges(live.revision.bodyMd, working.revision.bodyMd));
    if (!same(live.revision.seo, working.revision.seo)) lines.push("Search & sharing details changed");
  } else if (a.revision !== b.revision) lines.push("The words changed");
  return lines;
}

/** The confirmation lists a handful of changes, then counts the rest. */
export function listedChanges(lines: string[], limit = MAX_CHANGE_LINES): { lines: string[]; more: number } {
  return { lines: lines.slice(0, limit), more: Math.max(0, lines.length - limit) };
}

/** "2 changes saved but not live yet", for the post panel. */
export function pendingLine(count: number): string {
  return `${plural(count, "change")} saved but not live yet`;
}
